import { requestCameraAccess, PermissionStatus } from "./Permission";

/** Error thrown by CameraManager.open() — carries the classified permission
 * status alongside the friendly message, so callers can decide whether a
 * failure is worth retrying without re-deriving it from the raw DOMException. */
export class CameraAccessError extends Error {
  constructor(message: string, public readonly status: PermissionStatus) {
    super(message);
    this.name = "CameraAccessError";
  }
}

export class CameraManager {
  private stream: MediaStream | null = null;
  private onInterrupted: (() => void) | null = null;
  private trackEndedHandlers: Array<() => void> = [];

  /**
   * Opens the camera via requestCameraAccess(), which maps browser errors
   * (denied, no device, in-use-by-another-app) to a clean message thrown here.
   */
  async open(constraints?: MediaStreamConstraints): Promise<MediaStream> {
    const defaults: MediaStreamConstraints = {
      video: {
        facingMode: "user",
        width: { ideal: 1280 },
        height: { ideal: 720 },
      },
      audio: false,
    };

    const { stream, permission } = await requestCameraAccess(constraints || defaults);
    if (!stream) {
      throw new CameraAccessError(permission.reason || "Unable to access the camera.", permission.status);
    }

    this.stream = stream;
    this._watchTracks();

    return this.stream;
  }

  getStream(): MediaStream | null {
    return this.stream;
  }

  /**
   * Adopt an externally-obtained stream (e.g. a pre-warmed getUserMedia
   * stream) so stop() also releases it. Without this, a stream assigned
   * directly by a caller instead of via open() is never tracked here and
   * stop() becomes a no-op for it, leaking the camera.
   */
  setStream(stream: MediaStream): void {
    this.stream = stream;
    this._watchTracks();
  }

  /**
   * Register a callback fired the moment any track in the current stream
   * ends unexpectedly (OS revokes camera permission mid-session, another
   * app steals the camera, device sleeps and the driver drops the feed).
   * Without this, a dead stream is silently invisible — the video element
   * just freezes on its last frame and face detection quietly fails
   * forever with no indication anything went wrong.
   */
  onStreamInterrupted(callback: () => void): void {
    this.onInterrupted = callback;
  }

  private _watchTracks(): void {
    this.trackEndedHandlers = [];
    if (!this.stream) return;
    this.stream.getTracks().forEach((track) => {
      const handler = () => {
        // A track we intentionally stopped (via stop()) also fires "ended" —
        // only treat this as an interruption if we still think we have a
        // live stream (stop() nulls this.stream before that would happen).
        if (this.stream) {
          this.onInterrupted?.();
        }
      };
      track.addEventListener("ended", handler);
      this.trackEndedHandlers.push(() => track.removeEventListener("ended", handler));
    });
  }

  stop(): void {
    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop());
      this.stream = null;
    }
    this.trackEndedHandlers.forEach((remove) => remove());
    this.trackEndedHandlers = [];
  }
}
